(() => {
  // Autoplay is held until window `load`: a slideshow advancing during page load churns
  // Speed Index and main-thread work (per-frame autoplay ticks + slide transitions), and
  // the first slide is on screen anyway.
  let pageLoaded = document.readyState === "complete";
  const onPageLoad = [];
  if (!pageLoaded) {
    window.addEventListener(
      "load",
      () => {
        pageLoaded = true;
        onPageLoad.forEach((cb) => cb());
      },
      { once: true }
    );
  }

  const playVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const prevSlide = swiper.slides[swiper.previousIndex];
    const activeSlide = swiper.slides[swiper.activeIndex];

    if (activeSlide) {
      const videoActive = activeSlide.querySelector(".slideshow__video");
      if (videoActive) {
        videoActive.play().catch((error) => {
          console.error("Error playing video:", error);
        });
      }
    }

    if (prevSlide && prevSlide !== activeSlide) {
      const videoPrev = prevSlide.querySelector(".slideshow__video");
      if (videoPrev) videoPrev.pause();
    }
  };

  const stopVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (activeSlide) {
      const videoActive = activeSlide.querySelector(".slideshow__video");
      if (videoActive) videoActive.pause();
    }
  };

  // Keep interactions enabled during transitions: with slide+loop, rapid nav can make loopFix
  // skip a transitionend and leave `animating` stuck true. Allowing follow-up navigation lets
  // Swiper self-heal on the next transition instead of freezing arrows/pagination.
  const getBaseParams = () => ({
    speed: 1000,
    allowTouchMove: true,
    watchSlidesProgress: true,
  });

  // Maps the `animation_type` setting to Swiper effect params for a given swiper role.
  // For `creative` the media swiper uses `creative` (slides stay stacked without moving) so a
  // JS-driven clip-path wipe can run on top, while the content swiper cross-fades.
  const getEffectParams = (box, role) => {
    const anim = box.dataset.animation || "slide";
    if (anim === "fade") {
      return { effect: "fade", fadeEffect: { crossFade: true } };
    }
    if (anim === "creative") {
      if (role === "media") {
        return {
          effect: "creative",
          speed: 500,
          creativeEffect: {
            // Identity transforms keep the slides stacked and still — the JS-driven clip-path
            // wipe is the only visible motion. The 0.99 opacity is an imperceptible real
            // transition: `effect: 'creative'` runs with virtualTranslate, so Swiper detects the
            // transition end from a slide `transitionend`. With an all-identity effect nothing
            // transitions, that event never fires, and the swiper locks up after the first slide.
            prev: {
              translate: [0, 0, 0],
              rotate: [0, 0, 0],
              scale: 1,
              opacity: 0.99,
            },
            next: {
              translate: [0, 0, 0],
              rotate: [0, 0, 0],
              scale: 1,
              opacity: 0.99,
            },
          },
        };
      }
      return { effect: "fade", fadeEffect: { crossFade: true }, speed: 500 };
    }
    return { effect: "slide" };
  };

  const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Looping behaviour by effect:
  // - `slide`: real `loop` (clones slides for seamless horizontal motion).
  // - `fade` / `creative`: `rewind` instead. These effects don't move horizontally, so wrapping
  //   back to the first slide looks identical to a seamless loop — but without slide clones, which
  //   with only 2-3 slides flash the section background mid-transition and break the loop.
  const applyLoopParams = (params, box) => {
    if (box.dataset.loop !== "true") return;
    if ((box.dataset.animation || "slide") === "slide") {
      params.loop = true;
      params.loopPreventsSliding = false;
    } else {
      params.rewind = true;
    }
  };

  const initMediaSwiper = (box) => {
    const el = box.querySelector(".slideshow__media-swiper");
    if (!el) return null;
    if (el.querySelectorAll(".swiper-slide").length < 2) return null;

    const params = getBaseParams();
    Object.assign(params, getEffectParams(box, "media"));

    applyLoopParams(params, box);

    if (box.dataset.autoplay === "true" && !prefersReducedMotion()) {
      const delay = Number(box.dataset.autoplayDuration || 5) * 1000;
      params.autoplay = {
        delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      };
    }

    params.on = {
      slideChange: function () {
        playVideo(this);
      },
    };

    return new Swiper(el, params);
  };

  const initContentSwiper = (box) => {
    const el = box.querySelector(".slideshow__content-swiper");
    if (!el) return null;
    if (el.querySelectorAll(".swiper-slide").length < 2) return null;

    const params = getBaseParams();
    Object.assign(params, getEffectParams(box, "content"));
    params.allowTouchMove = false;

    applyLoopParams(params, box);

    return new Swiper(el, params);
  };

  const linkSwipers = (mediaSwiper, contentSwiper) => {
    if (!mediaSwiper || !contentSwiper) return;
    mediaSwiper.controller.control = contentSwiper;
    contentSwiper.controller.control = mediaSwiper;
  };

  // Manual navigation (wheel / arrows / pagination) doesn't restart the autoplay countdown, so the
  // slide we land on inherits the previous slide's leftover time and can auto-advance immediately.
  // Restart it to give the new slide a full delay. Guarded by `running` so we don't resurrect
  // autoplay that the lifecycle paused (off-screen / keyboard focus).
  const resetAutoplay = (mediaSwiper) => {
    const autoplay = mediaSwiper && mediaSwiper.autoplay;
    if (!autoplay || !autoplay.running) return;
    autoplay.stop();
    autoplay.start();
  };

  const initPagination = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    const paginationEl = box.querySelector(".slideshow__pagination");
    if (!paginationEl) return;

    const bullets = Array.from(
      paginationEl.querySelectorAll(".slideshow__bullet")
    );
    if (bullets.length === 0) return;

    const getActiveRealIndex = () =>
      mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;

    const hasAutoplay = box.dataset.autoplay === "true";

    const setActiveBullet = (realIndex) => {
      bullets.forEach((bullet, idx) => {
        const isActive = idx === realIndex;
        bullet.classList.toggle("is-active", isActive);
        bullet.setAttribute("aria-selected", isActive ? "true" : "false");
        // Under autoplay the bar is a pure CSS animation ((re)started by `is-active` landing on
        // the bullet) — no inline vars. The `--bullet-progress` var dance is no-autoplay only.
        if (hasAutoplay) return;
        if (isActive) {
          bullet.style.removeProperty("--bullet-progress");
        } else {
          bullet.style.setProperty("--bullet-progress", "0");
        }
      });
    };

    bullets.forEach((bullet) => {
      bullet.addEventListener("click", () => {
        const idx = Number(bullet.dataset.slideIndex);
        if (Number.isNaN(idx)) return;
        if (mediaSwiper.params.loop) {
          mediaSwiper.slideToLoop(idx);
        } else {
          mediaSwiper.slideTo(idx);
        }
        resetAutoplay(mediaSwiper);
      });
    });

    // Track navigation direction so the progress bar fills/empties with it (CSS `.is-nav-prev`).
    // Compare real indices, with the loop/rewind wrap handled explicitly (last→first is forward,
    // first→last is back).
    let lastRealIndex = getActiveRealIndex();
    mediaSwiper.on("slideChange", () => {
      const realIndex = getActiveRealIndex();
      const total = bullets.length;
      let isPrev;
      if (lastRealIndex === total - 1 && realIndex === 0) {
        isPrev = false;
      } else if (lastRealIndex === 0 && realIndex === total - 1) {
        isPrev = true;
      } else {
        isPrev = realIndex < lastRealIndex;
      }
      lastRealIndex = realIndex;
      box.classList.toggle("is-nav-prev", isPrev);
      setActiveBullet(realIndex);
    });

    setActiveBullet(getActiveRealIndex());
  };

  // Mobile pagination peek-strip: below $lg the pagination scrolls horizontally with scroll-snap.
  // Settling the strip on another tab drives the whole slideshow; a slide change scrolls the strip
  // back to the active tab. The active-vs-nearest check keeps the two in sync without a feedback loop.
  const initPaginationPeek = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    const strip = box.querySelector(".slideshow__pagination");
    if (!strip) return;
    const bullets = Array.from(strip.querySelectorAll(".slideshow__bullet"));
    if (bullets.length < 2) return;

    const mq = window.matchMedia("(max-width: 989px)");
    const getActiveIndex = () =>
      mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;

    let scrollTimer = null;

    const scrollToActive = (smooth) => {
      const target = bullets[getActiveIndex()];
      if (!target) return;
      const delta =
        target.getBoundingClientRect().left -
        strip.getBoundingClientRect().left;
      strip.scrollBy({ left: delta, behavior: smooth ? "smooth" : "auto" });
    };

    // When the user settles the strip on a different tab, drive the whole slideshow to it.
    const onScrollSettled = () => {
      if (!mq.matches) return;
      const maxScroll = strip.scrollWidth - strip.clientWidth;
      let nearest;
      if (maxScroll > 0 && strip.scrollLeft >= maxScroll - 2) {
        nearest = bullets.length - 1;
      } else if (strip.scrollLeft <= 2) {
        nearest = 0;
      } else {
        const base = strip.getBoundingClientRect().left;
        let min = Infinity;
        nearest = 0;
        bullets.forEach((bullet, i) => {
          const distance = Math.abs(bullet.getBoundingClientRect().left - base);
          if (distance < min) {
            min = distance;
            nearest = i;
          }
        });
      }
      if (nearest === getActiveIndex()) return;
      if (mediaSwiper.params.loop) {
        mediaSwiper.slideToLoop(nearest);
      } else {
        mediaSwiper.slideTo(nearest);
      }
      resetAutoplay(mediaSwiper);
    };

    strip.addEventListener(
      "scroll",
      () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(onScrollSettled, 120);
      },
      { passive: true }
    );

    // A slide change (autoplay, media swipe, tab tap) keeps the strip aligned to the active tab.
    mediaSwiper.on("slideChange", () => {
      if (mq.matches) scrollToActive(true);
    });

    // Align the strip to the active tab on load and when entering the mobile breakpoint.
    if (mq.matches) scrollToActive(false);
    mq.addEventListener("change", (event) => {
      if (event.matches) scrollToActive(false);
    });
  };

  // Drives autoplay from viewport visibility + keyboard focus. Hover-pause intentionally omitted:
  // the section is typically fullscreen, so a hover-pause would mean autoplay never runs.
  // Using autoplay.stop()/start() instead of pause()/resume() because pause() is cleared by the
  // next slideTo (e.g. pagination click), making a hovered pause "revive" after manual navigation.
  const initLifecycle = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    const hasAutoplay = box.dataset.autoplay === "true";

    let isVisible = false;
    let isFocusWithin = false;

    const sync = () => {
      if (isVisible) playVideo(mediaSwiper);
      else stopVideo(mediaSwiper);

      if (!hasAutoplay || !mediaSwiper.autoplay) return;
      const shouldRun = isVisible && !isFocusWithin && pageLoaded;
      if (shouldRun) {
        mediaSwiper.autoplay.start();
      } else {
        mediaSwiper.autoplay.stop();
      }
      // The CSS progress-bar countdown runs only while autoplay actually runs.
      box.classList.toggle("is-autoplay-running", mediaSwiper.autoplay.running);
    };

    // Swiper starts autoplay during init; stop it in the same task — the observer below and the
    // window `load` callback re-enable it once the section is visible and the page has loaded.
    sync();
    onPageLoad.push(sync);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        isVisible = entry.isIntersecting;
        sync();
      });
    });
    observer.observe(box);

    // Only keyboard focus pauses autoplay. Mouse-clicking a pagination tab also gives it focus,
    // but `:focus-visible` will be false in that case, so we skip it.
    box.addEventListener("focusin", (event) => {
      const target = event.target;
      if (!target || typeof target.matches !== "function") return;
      if (!target.matches(":focus-visible")) return;
      isFocusWithin = true;
      sync();
    });
    box.addEventListener("focusout", (event) => {
      if (!isFocusWithin) return;
      if (box.contains(event.relatedTarget)) return;
      isFocusWithin = false;
      sync();
    });
  };

  // Exposes the info bar height as a CSS var on the section root for overlays anchored above it.
  const initInfoHeightVar = (box) => {
    const info = box.querySelector(".slideshow__info");
    if (!info) {
      box.style.setProperty("--slideshow-info-height", "0px");
      return;
    }
    const update = () => {
      box.style.setProperty(
        "--slideshow-info-height",
        `${info.offsetHeight}px`
      );
    };
    update();
    if (typeof ResizeObserver === "function") {
      const ro = new ResizeObserver(update);
      ro.observe(info);
    } else {
      window.addEventListener("resize", update);
    }
  };

  const initCursorArrows = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    if (box.dataset.cursorArrows !== "true") return;
    if (typeof window.initCursorArrowZone !== "function") return;

    const zone = box.querySelector(".slideshow__arrow-zone");
    if (!zone) return;

    // Loop / rewind always wrap, so only a finite slider can reach an inert end: there the click is
    // a no-op and CSS marks the arrow not-allowed (see `.is-disabled`).
    const finite = !mediaSwiper.params.loop && !mediaSwiper.params.rewind;

    const nav = window.initCursorArrowZone(zone, {
      onNavigate: (direction) => {
        if (direction === "next") {
          mediaSwiper.slideNext();
        } else {
          mediaSwiper.slidePrev();
        }
        resetAutoplay(mediaSwiper);
      },
      isEndReached: (direction) => {
        if (!finite) return false;
        return direction === "prev"
          ? mediaSwiper.isBeginning
          : mediaSwiper.isEnd;
      },
    });

    if (finite) mediaSwiper.on("slideChange", nav.sync);
  };

  // Creative animation: the incoming media slide is wiped in bottom-to-top via clip-path, while
  // the slide we came from stays fully visible underneath as the underlay; all others are clipped
  // away. `effect: 'creative'` keeps the slides stacked (and the active one on top) without moving
  // them. The clip-path lives on `__media`, not the slide itself — Swiper drives `transition` on
  // the slide, so keeping our transition on a child element avoids a conflict.
  const initCreativeMedia = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    if (box.dataset.animation !== "creative") return;

    const HIDDEN = "inset(100% 0 0 0)";
    const SHOWN = "inset(0 0 0 0)";

    const getMedia = (slide) =>
      slide ? slide.querySelector(".slideshow__media") : null;

    const setInstant = (media, value) => {
      if (!media) return;
      media.style.transition = "none";
      media.style.clipPath = value;
    };

    if (prefersReducedMotion()) {
      // No wipe: Swiper's creative z-index keeps the active slide on top for an instant cut.
      mediaSwiper.slides.forEach((slide) => setInstant(getMedia(slide), SHOWN));
      return;
    }

    mediaSwiper.slides.forEach((slide, i) => {
      setInstant(
        getMedia(slide),
        i === mediaSwiper.activeIndex ? SHOWN : HIDDEN
      );
    });

    // Only wipe on a real slide change. `slideChange` also fires when Swiper re-shuffles its
    // looped slides after a resize (e.g. a dynamic announcement bar changing the section height
    // on scroll) — there the logical slide is unchanged, so replaying the wipe would be a glitch.
    let lastRealIndex = mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;

    mediaSwiper.on("slideChange", () => {
      const realIndex = mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;
      if (realIndex === lastRealIndex) return;
      lastRealIndex = realIndex;

      const { slides, activeIndex, previousIndex } = mediaSwiper;
      slides.forEach((slide, i) => {
        const media = getMedia(slide);
        if (!media) return;
        if (i === activeIndex) {
          // Force a reflow so the wipe always starts from the fully-clipped state.
          media.style.transition = "none";
          media.style.clipPath = HIDDEN;
          void media.offsetWidth;
          media.style.transition =
            "clip-path 0.7s cubic-bezier(0.45, 0.75, 0.1, 0.9)";
          media.style.clipPath = SHOWN;
        } else if (i === previousIndex) {
          setInstant(media, SHOWN);
        } else {
          setInstant(media, HIDDEN);
        }
      });
    });
  };

  // Two-finger horizontal trackpad swipe → prev/next. Only horizontal-dominant `wheel` gestures are
  // captured (vertical wheel keeps scrolling the page); for those we `preventDefault` so the browser
  // back/forward swipe doesn't fire over the slideshow. A trackpad emits a long momentum stream after
  // the flick, so a navigation starts a fixed cooldown (NOT reset per event — that would keep it
  // locked for the whole inertia): one slide per swipe, and by the time the cooldown ends the inertia
  // tail is below the `8` threshold and won't re-trigger. Listener is on the section root, not the
  // media swiper, since the info/hover layers sit above it and would otherwise swallow the event.
  const COOLDOWN = 700;
  const initWheelNav = (box, mediaSwiper) => {
    if (!mediaSwiper) return;

    let locked = false;

    box.addEventListener(
      "wheel",
      (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();

        if (locked || Math.abs(event.deltaX) < 8) return;
        locked = true;
        window.setTimeout(() => {
          locked = false;
        }, COOLDOWN);

        if (event.deltaX > 0) {
          mediaSwiper.slideNext();
        } else {
          mediaSwiper.slidePrev();
        }
        resetAutoplay(mediaSwiper);
      },
      { passive: false }
    );
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains("slideshow-section")) return;

    const box = section.querySelector(".slideshow");
    if (!box) return;

    const mediaSwiper = initMediaSwiper(box);
    const contentSwiper = initContentSwiper(box);

    linkSwipers(mediaSwiper, contentSwiper);
    initCreativeMedia(box, mediaSwiper);
    initPagination(box, mediaSwiper);
    initPaginationPeek(box, mediaSwiper);
    initInfoHeightVar(box);
    initCursorArrows(box, mediaSwiper);
    initWheelNav(box, mediaSwiper);
    initLifecycle(box, mediaSwiper);
  };

  initSection(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", function (event) {
    initSection(event.target);
  });
})();
